/**
 * VIP激活页面
 * 支持订单号激活和授权码激活两种方式
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { vipApiService } from '@/services/vipApi.js';
import { verifyVipCode, activateVipCode } from '@/services/vipCodeApi.js';
import { useVIPGiftRules } from '@/hooks/useVIP.js';
import { VIPBadge } from '@/components/vip';
import type { VIPActivationInput } from '@/types/vip.js';
import { KeyRound, Receipt, CheckCircle, Loader2 } from 'lucide-react';

export function VIPActivationPage() {
  const navigate = useNavigate();
  const { rules, loading: rulesLoading } = useVIPGiftRules();

  // 订单号激活
  const [formData, setFormData] = useState<VIPActivationInput>({
    productCode: '',
    orderNo: '',
  });

  // 授权码激活
  const [activationCode, setActivationCode] = useState('');
  const [codeInfo, setCodeInfo] = useState<{
    code: string;
    name: string;
    vip_level: number;
    vip_level_name: string;
    duration_months: number;
  } | null>(null);
  const [verifying, setVerifying] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // 订单号激活处理
  const handleOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await vipApiService.activateVIP(formData);

      // 更新localStorage中的权限信息
      if (result.permissionInfo) {
        localStorage.setItem('vipPermissions', JSON.stringify(result.permissionInfo));
      }

      setSuccess(true);
      setSuccessMessage('VIP权限已激活，正在跳转到个人资料页...');

      // 3秒后跳转
      setTimeout(() => {
        navigate('/profile');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || '激活失败');
    } finally {
      setLoading(false);
    }
  };

  // 验证授权码
  const handleVerifyCode = async () => {
    if (!activationCode.trim()) {
      setError('请输入授权码');
      return;
    }

    setError('');
    setVerifying(true);
    setCodeInfo(null);

    try {
      const result = await verifyVipCode(activationCode.trim());
      if (result.success && result.data) {
        setCodeInfo(result.data);
      } else {
        setError(result.message || '授权码无效');
      }
    } catch (err: any) {
      setError(err.message || '验证授权码失败');
    } finally {
      setVerifying(false);
    }
  };

  // 授权码激活处理
  const handleCodeActivate = async () => {
    if (!activationCode.trim()) {
      setError('请输入授权码');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await activateVipCode(activationCode.trim());
      if (result.success && result.data) {
        let message = `恭喜！您已成功激活${result.data.vip_level_name}，`;
        message += `有效期至 ${new Date(result.data.expire_at).toLocaleDateString('zh-CN')}`;
        if (result.data.converted_months) {
          message += `（包含转换时间 ${result.data.converted_months} 个月）`;
        }
        
        setSuccess(true);
        setSuccessMessage(message);

        // 3秒后跳转
        setTimeout(() => {
          navigate('/profile');
        }, 3000);
      } else {
        setError(result.message || '激活失败');
      }
    } catch (err: any) {
      setError(err.message || '激活授权码失败');
    } finally {
      setLoading(false);
    }
  };

  if (rulesLoading) {
    return (
      <LayoutWithSidebar title="激活VIP">
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </LayoutWithSidebar>
    );
  }

  if (success) {
    return (
      <LayoutWithSidebar title="激活VIP">
        <div className="flex flex-1 items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <CardTitle className="text-center text-green-600">激活成功!</CardTitle>
              <CardDescription className="text-center">
                {successMessage}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar title="激活VIP">
      <div className="flex flex-1 flex-col p-4 bg-gradient-to-br from-yellow-50 to-orange-50">
          <div className="mx-auto max-w-4xl w-full">
            <div className="mb-6 text-center">
              <p className="text-gray-600">
                选择您喜欢的方式激活VIP会员
              </p>
            </div>

        <Tabs defaultValue="code" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="code" className="flex items-center gap-2">
              <KeyRound className="h-4 w-4" />
              授权码激活
            </TabsTrigger>
            <TabsTrigger value="order" className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              订单号激活
            </TabsTrigger>
          </TabsList>

          {/* 授权码激活 */}
          <TabsContent value="code">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <KeyRound className="h-5 w-5 text-purple-600" />
                    授权码激活
                  </CardTitle>
                  <CardDescription>输入您的VIP授权码激活会员</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="activationCode">授权码</Label>
                    <div className="flex gap-2">
                      <Input
                        id="activationCode"
                        type="text"
                        placeholder="VIP-XXXX-XXXX-XXXX"
                        value={activationCode}
                        onChange={(e) => {
                          setActivationCode(e.target.value.toUpperCase());
                          setCodeInfo(null);
                          setError('');
                        }}
                        disabled={loading}
                        className="font-mono"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleVerifyCode}
                        disabled={verifying || loading || !activationCode.trim()}
                      >
                        {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : '验证'}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                      授权码格式：VIP-XXXX-XXXX-XXXX 或 VVIP-XXXX-XXXX-XXX
                    </p>
                  </div>

                  {codeInfo && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-green-800">授权码有效</span>
                        <VIPBadge level={codeInfo.vip_level} />
                      </div>
                      <div className="text-sm text-green-700 space-y-1">
                        <div>名称：{codeInfo.name}</div>
                        <div>类型：{codeInfo.vip_level_name}</div>
                        <div>时长：{codeInfo.duration_months} 个月</div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      disabled={loading}
                    >
                      取消
                    </Button>
                    <Button
                      onClick={handleCodeActivate}
                      disabled={loading || !activationCode.trim()}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          激活中...
                        </>
                      ) : (
                        '激活VIP'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* 激活说明 */}
              <Card>
                <CardHeader>
                  <CardTitle>激活说明</CardTitle>
                  <CardDescription>了解授权码激活规则</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3 text-sm">
                    <div className="flex gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">1</span>
                      <div>
                        <div className="font-medium">时间叠加</div>
                        <div className="text-gray-500">同等级授权码激活后，时间会自动叠加</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">2</span>
                      <div>
                        <div className="font-medium">VIP升级VVIP</div>
                        <div className="text-gray-500">VIP用户使用VVIP码时，剩余VIP时间会按比例转换（2个月VIP = 1个月VVIP）</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold">3</span>
                      <div>
                        <div className="font-medium">永久有效</div>
                        <div className="text-gray-500">授权码没有使用期限，可以随时激活</div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <div className="text-sm text-gray-600">
                      <strong>VIP授权码</strong>：VIP-XXXX-XXXX-XXXX
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <strong>VVIP授权码</strong>：VVIP-XXXX-XXXX-XXX
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* 订单号激活 */}
          <TabsContent value="order">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-blue-600" />
                    订单号激活
                  </CardTitle>
                  <CardDescription>输入产品代码和订单号激活VIP会员</CardDescription>
                </CardHeader>
                <form onSubmit={handleOrderSubmit}>
                  <CardContent className="space-y-4">
                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="productCode">产品代码</Label>
                      <Input
                        id="productCode"
                        type="text"
                        placeholder="ring_299"
                        value={formData.productCode}
                        onChange={(e) => setFormData({ ...formData, productCode: e.target.value })}
                        required
                        disabled={loading}
                      />
                      <p className="text-xs text-gray-500">
                        产品代码: ring_299, gateway_399, ring_599, combo_any
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="orderNo">订单号 *</Label>
                      <Input
                        id="orderNo"
                        type="text"
                        placeholder="请输入拼多多/淘宝订单号"
                        value={formData.orderNo}
                        onChange={(e) => setFormData({ ...formData, orderNo: e.target.value })}
                        required
                        disabled={loading}
                      />
                      <p className="text-xs text-gray-500">
                        请输入在拼多多/淘宝购买商品后获得的订单号
                      </p>
                    </div>
                  </CardContent>

                  <div className="flex items-center justify-between p-6 pt-0">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      disabled={loading}
                    >
                      取消
                    </Button>
                    <Button type="submit" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          激活中...
                        </>
                      ) : (
                        '激活VIP'
                      )}
                    </Button>
                  </div>
                </form>
              </Card>

              {/* 产品列表 */}
              <Card>
                <CardHeader>
                  <CardTitle>产品列表</CardTitle>
                  <CardDescription>点击选择产品代码</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {rules.map((rule) => (
                    <div
                      key={rule.product_code}
                      className={`flex items-center justify-between rounded-lg border p-4 cursor-pointer transition-colors ${
                        formData.productCode === rule.product_code
                          ? 'border-blue-500 bg-blue-50'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setFormData({ ...formData, productCode: rule.product_code })}
                    >
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{rule.product_name}</div>
                        <div className="mt-1 text-sm text-gray-600">
                          ¥{rule.price}
                          <span className="ml-2">
                            → VIP {rule.gift_vip_level === 1 ? '1年' : '2年'} ({rule.gift_duration_days}天)
                          </span>
                        </div>
                      </div>
                      <VIPBadge level={rule.gift_vip_level} />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </LayoutWithSidebar>
  );
}
